"use client";

import Link from "next/link";
import Image from "next/image";
import { getDisplayStatus, getStatusColorHero } from "@/lib/campaign-status";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  orgName: string | null;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  participantCount: number;
  clueCount: number;
}

interface FeaturedCampaignSectionProps {
  tenant: {
    slug: string;
    primaryColor: string;
    secondaryColor?: string;
    accentColor?: string;
  };
  campaign: Campaign | null;
  title?: string | null;
  description?: string | null;
  whitelabel?: boolean;
}

export function FeaturedCampaignSection({
  tenant,
  campaign,
  title,
  description,
  whitelabel,
}: FeaturedCampaignSectionProps) {
  if (!campaign) {
    return null;
  }

  const displayTitle = title || "Featured Experience";

  return (
    <div className="max-w-4xl mx-auto mb-16">
      <div
        className="rounded-xl shadow-xl overflow-hidden bg-white"
        style={whitelabel ? { backgroundColor: 'var(--theme-bg, #FFFFFF)' } : undefined}
      >
        {/* Hero Image Section with Overlaid Text */}
        <div className="relative h-72 md:h-80">
          {campaign.coverImageUrl ? (
            <Image
              src={campaign.coverImageUrl}
              alt={campaign.title}
              fill
              className="object-cover"
              unoptimized
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${whitelabel ? (tenant.primaryColor || '#3B82F6') : '#3B82F6'} 0%, ${whitelabel ? (tenant.accentColor || '#8B5CF6') : '#8B5CF6'} 100%)`
              }}
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

          <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full backdrop-blur-sm text-white"
                  style={{
                    backgroundColor: whitelabel
                      ? `${tenant.secondaryColor || tenant.primaryColor || '#3B82F6'}CC`
                      : '#3B82F6CC'
                  }}
                >
                  {displayTitle}
                </span>
              </div>
              <span
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full backdrop-blur-sm ${getStatusColorHero(getDisplayStatus(campaign))}`}
              >
                {getDisplayStatus(campaign)}
              </span>
            </div>

            <div>
              <h3
                className="text-3xl md:text-4xl font-bold text-white mb-2"
                style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)' }}
              >
                {campaign.title}
              </h3>
              {campaign.orgName && (
                <p
                  className="text-white/90 text-sm md:text-base font-medium"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
                >
                  Hosted by {campaign.orgName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content Below Image */}
        <div className="p-6 md:p-8">
          {description && (
            <p
              className="mb-4 text-sm text-gray-500"
              style={whitelabel ? { color: 'var(--theme-text, #6B7280)', opacity: 0.8 } : undefined}
            >
              {description}
            </p>
          )}

          <p
            className="text-lg mb-6 whitespace-pre-line line-clamp-4 text-gray-600"
            style={whitelabel ? { color: 'var(--theme-text, #4B5563)' } : undefined}
          >
            {campaign.description}
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div
              className="text-center p-4 rounded-lg bg-gray-100"
              style={whitelabel ? { backgroundColor: 'var(--theme-bg, #F3F4F6)' } : undefined}
            >
              <div
                className="text-2xl font-bold text-blue-600"
                style={whitelabel ? { color: tenant.primaryColor || '#3B82F6' } : undefined}
              >
                {campaign.clueCount}
              </div>
              <div
                className="text-sm text-gray-500"
                style={whitelabel ? { color: 'var(--theme-text, #6B7280)' } : undefined}
              >
                Locations
              </div>
            </div>
            <div
              className="text-center p-4 rounded-lg bg-gray-100"
              style={whitelabel ? { backgroundColor: 'var(--theme-bg, #F3F4F6)' } : undefined}
            >
              <div
                className="text-2xl font-bold text-emerald-500"
                style={whitelabel ? { color: tenant.accentColor || '#10B981' } : undefined}
              >
                {campaign.participantCount}
              </div>
              <div
                className="text-sm text-gray-500"
                style={whitelabel ? { color: 'var(--theme-text, #6B7280)' } : undefined}
              >
                Participants
              </div>
            </div>
            <div
              className="text-center p-4 rounded-lg col-span-2 md:col-span-1 bg-gray-100"
              style={whitelabel ? { backgroundColor: 'var(--theme-bg, #F3F4F6)' } : undefined}
            >
              <div
                className="text-2xl font-bold truncate text-indigo-600"
                style={whitelabel ? { color: 'var(--theme-secondary, #8B5CF6)' } : undefined}
              >
                {campaign.orgName || 'Organization'}
              </div>
              <div
                className="text-sm text-gray-500"
                style={whitelabel ? { color: 'var(--theme-text, #6B7280)' } : undefined}
              >
                Hosted by
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <Link
              href={`/${tenant.slug}/campaigns/${campaign.slug}`}
              className="inline-flex items-center justify-center px-8 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 hover:scale-105 shadow-lg bg-blue-600"
              style={whitelabel ? { backgroundColor: tenant.primaryColor || '#3B82F6' } : undefined}
            >
              Join Experience
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
